import { useState } from 'react'
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Combine,
  Copy,
  Download,
  FileCheck2,
  Link2,
  ScanText,
  Sparkles,
  Type,
  X,
} from 'lucide-react'
import { applyFinishSelections, buildFinishPlan } from '../lib/ai'
import { CARD_COLORS } from '../data'
import { buildManuscript, downloadText, safeFilename, visibleSnippet } from '../lib/utils'
import { CoverArt } from './Shared'

const STEPS = [
  { id: 'merge', label: '整合', en: 'CONSOLIDATE', description: '把重复的线索收到一张卡上', icon: Combine },
  { id: 'proof', label: '校对', en: 'PROOFREAD', description: '逐句检查错字与标点', icon: Type },
  { id: 'finish', label: '定稿', en: 'MANUSCRIPT', description: '生成可以带走的整册文稿', icon: BookOpen },
]

function diffParts(before, after) {
  let start = 0
  while (start < before.length && start < after.length && before[start] === after[start]) start += 1
  let endBefore = before.length - 1
  let endAfter = after.length - 1
  while (endBefore >= start && endAfter >= start && before[endBefore] === after[endAfter]) {
    endBefore -= 1
    endAfter -= 1
  }
  return {
    prefix: before.slice(0, start),
    removed: before.slice(start, endBefore + 1),
    added: after.slice(start, endAfter + 1),
    suffix: before.slice(endBefore + 1),
  }
}

export default function FinishStudio({ project, onClose, onApply, notify, onOpenRoundtable }) {
  const [plan] = useState(() => buildFinishPlan(project))
  const [step, setStep] = useState(0)
  const [selectedMerges, setSelectedMerges] = useState(() => new Set(plan.merges.map((merge) => merge.id)))
  const [selectedProofs, setSelectedProofs] = useState(() => new Set(plan.proofreads.map((issue) => issue.id)))
  const [result, setResult] = useState(null)
  const [applied, setApplied] = useState(false)
  const [copied, setCopied] = useState(false)

  const mergeCount = selectedMerges.size
  const proofCount = selectedProofs.size
  const estimatedCards = project.cards.length - mergeCount
  const activeStep = STEPS[step]
  const doc = result?.doc || buildManuscript(project)

  function toggleMerge(id) {
    if (applied) return
    setSelectedMerges((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleProof(id) {
    if (applied) return
    setSelectedProofs((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function applyAll() {
    if (applied) return
    const output = applyFinishSelections(project, plan, [...selectedMerges], [...selectedProofs])
    onApply(output)
    setResult(output)
    setApplied(true)
    setStep(2)
    notify('定稿已生成：' + output.cards.length + ' 张卡片 · ' + output.doc.words + ' 字。')
  }

  async function copyManuscript() {
    try {
      await navigator.clipboard.writeText(result?.doc?.markdown || doc.markdown)
      setCopied(true)
      notify('整册文稿已复制到剪贴板。')
      window.setTimeout(() => setCopied(false), 2200)
    } catch (error) {
      notify('复制失败，请使用“导出 Markdown”。')
    }
  }

  function downloadManuscript() {
    const target = result?.doc || doc
    downloadText(safeFilename(project.title) + '-定稿.md', target.markdown)
    notify('已导出 Markdown 文稿。')
  }

  return (
    <div className="finish-backdrop">
      <div className="finish-panel" role="dialog" aria-modal="true" aria-label="收束工作室">
        <header className="finish-header">
          <div className="finish-header__left">
            <span className="finish-mark"><FileCheck2 size={20} /></span>
            <div>
              <div className="finish-eyebrow">THE BINDERY / 收束工作室</div>
              <h2>把 {project.cards.length} 张卡片，装订成一册。</h2>
            </div>
          </div>
          <div className="finish-header__right">
            <span className="finish-ai-status"><Sparkles size={14} /> AI 已读完你的手稿</span>
            <button className="icon-btn icon-btn--light" onClick={onClose} aria-label="关闭收束工作室"><X size={18} /></button>
          </div>
        </header>

        <div className="finish-body">
          <aside className="finish-side">
            <div className="finish-book">
              <CoverArt project={project} variant="finish-thumb" />
              <div>
                <span>{project.type}</span>
                <strong>{project.title}</strong>
                <em>{plan.stats.cards} 张卡片 · {plan.stats.words} 字</em>
              </div>
            </div>

            <div className="finish-steps">
              {STEPS.map((item, index) => {
                const Icon = item.icon
                const count = index === 0 ? plan.merges.length : index === 1 ? plan.proofreads.length : estimatedCards
                const isDone = applied && index < 2
                return (
                  <button
                    key={item.id}
                    className={'finish-step ' + (step === index ? 'is-active' : '') + (isDone ? ' is-done' : '')}
                    onClick={() => { if (!applied || index === 2) setStep(index) }}
                    disabled={applied && index < 2}
                  >
                    <span className="finish-step__index">{isDone ? <Check size={13} /> : '0' + (index + 1)}</span>
                    <div>
                      <strong>{item.label}<em>{item.en}</em></strong>
                      <p>{item.description}</p>
                    </div>
                    <span className="finish-step__count">{index < 2 ? count : estimatedCards}</span>
                  </button>
                )
              })}
            </div>

            <div className="finish-side-note">
              <span>“</span>
              <p>收束不是删减。是把说过很多遍的那句话，只留下最准确的一次。</p>
              <em>— 页屿编辑手记</em>
            </div>

            {!applied ? (
              <div className="finish-side-summary">
                <div><span>已选择</span><strong>{mergeCount + proofCount}</strong><em>项修改</em></div>
                <div><span>预计留下</span><strong>{estimatedCards}</strong><em>张卡片</em></div>
              </div>
            ) : (
              <div className="finish-side-summary is-complete">
                <CheckCircle2 size={18} />
                <div><strong>定稿已生成</strong><span>{result.doc.count} 张卡片 · {result.doc.words} 字</span></div>
              </div>
            )}
          </aside>

          <main className="finish-content">
            <div className="finish-content-head">
              <div>
                <span className="finish-step-label">STEP 0{step + 1} / {activeStep.en}</span>
                <h3>{step === 0 ? '先处理重复，再留住差异。' : step === 1 ? '让句子干净，但不失去语气。' : applied ? '你的定稿已经可以带走了。' : '把散落的卡片收成一册。'}</h3>
              </div>
              <span className="finish-result-chip">
                {step === 0 ? plan.merges.length + ' 组待决定' : step === 1 ? plan.proofreads.length + ' 处可修' : applied ? '已完成' : '待生成'}
              </span>
            </div>

            {step === 0 ? (
              <div className="finish-section">
                <div className="finish-section-intro">
                  <span>AI 发现 {plan.merges.length} 组语义重合的卡片。</span>
                  <p>合并会保留第一张卡片，把另一张里独有的段落接进来。重复的句子会被自动跳过。</p>
                </div>

                {plan.merges.length ? plan.merges.map((merge) => {
                  const selected = selectedMerges.has(merge.id)
                  return (
                    <div className={'merge-option ' + (selected ? 'is-selected' : '')} key={merge.id} onClick={() => toggleMerge(merge.id)}>
                      <button className={'finish-check ' + (selected ? 'is-checked' : '')} aria-label={selected ? '取消选择合并' : '选择合并'} onClick={(event) => { event.stopPropagation(); toggleMerge(merge.id) }}>
                        {selected ? <Check size={12} /> : null}
                      </button>
                      <div className="merge-option__body">
                        <div className="merge-option__top">
                          <span className="match-pill">文字重合 {merge.similarity}%</span>
                          <span className="merge-option__reason">{merge.reason}</span>
                        </div>
                        <div className="merge-pair-preview">
                          {merge.cards.map((card) => (
                            <div className="mini-manuscript-card" key={card.id} style={{ '--card-tint': CARD_COLORS[card.color] || CARD_COLORS.paper }}>
                              <span>{card.act?.split(' · ')[0]}</span>
                              <strong>{card.title}</strong>
                              <p>{visibleSnippet(card.content, 64)}</p>
                            </div>
                          ))}
                          <div className="merge-arrow"><Combine size={16} /></div>
                          <div className="merge-outcome">
                            <span>合并为</span>
                            <strong>《{merge.title}》</strong>
                            <em>保留 {merge.tags.length} 个标签 · 去掉重复段落</em>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }) : (
                  <div className="finish-empty">
                    <CheckCircle2 size={26} />
                    <h4>没有需要合并的卡片。</h4>
                    <p>每张卡片都保留了独立的任务。可以把注意力放到校对和定稿上。</p>
                  </div>
                )}

                <div className="structure-notes">
                  <div className="finish-minihead"><span>结构观察</span><em>不会自动修改</em></div>
                  {plan.structure.map((note) => (
                    <div className={'structure-note tone-' + note.tone} key={note.id}>
                      <span className="structure-note__icon">
                        {note.tone === 'good' ? <CheckCircle2 size={15} /> : note.tone === 'warn' ? <AlertTriangle size={15} /> : note.tone === 'accent' ? <Sparkles size={15} /> : <Link2 size={15} />}
                      </span>
                      <div>
                        <strong>{note.title}</strong>
                        <p>{note.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {step === 1 ? (
              <div className="finish-section">
                <div className="finish-section-intro">
                  <span>AI 逐段读完了 {plan.stats.cards} 张卡片，找到 {plan.proofreads.length} 处可以确定的文字问题。</span>
                  <p>只标出可以被规则确认的改动。语气、节奏与有意的重复不会被自动改掉。</p>
                </div>
                {plan.proofreads.length ? (
                  <div className="proof-list">
                    {plan.proofreads.map((issue) => {
                      const selected = selectedProofs.has(issue.id)
                      const parts = diffParts(issue.before, issue.after)
                      return (
                        <div className={'proof-item ' + (selected ? 'is-selected' : '')} key={issue.id} onClick={() => toggleProof(issue.id)}>
                          <button className={'finish-check ' + (selected ? 'is-checked' : '')} aria-label={selected ? '取消选择校对' : '选择校对'} onClick={(event) => { event.stopPropagation(); toggleProof(issue.id) }}>
                            {selected ? <Check size={12} /> : null}
                          </button>
                          <div className="proof-item__body">
                            <div className="proof-item__meta">
                              <span className="proof-card-name"><Type size={12} />《{issue.cardTitle}》</span>
                              <em>{issue.reason}</em>
                            </div>
                            <div className="proof-diff">
                              <div className="proof-line proof-line--before">
                                <span>原文</span>
                                <p>{parts.prefix}<del>{parts.removed}</del>{parts.suffix}</p>
                              </div>
                              <div className="proof-line proof-line--after">
                                <span>改为</span>
                                <p>{parts.prefix}<ins>{parts.added}</ins>{parts.suffix}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="finish-empty">
                    <CheckCircle2 size={26} />
                    <h4>文字很干净。</h4>
                    <p>没有发现可以确定的错字或标点问题。建议保留现在的语气。</p>
                  </div>
                )}
                <div className="proof-policy">
                  <ScanText size={16} />
                  <div>
                    <strong>校对边界</strong>
                    <p>页屿只修正确认的错误，不替你改写句子。像“，，”和“既使”这类问题会被标记；“潮湿”“盐”或刻意的短句不会被当作重复。</p>
                  </div>
                </div>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="finish-final">
                <div className={'final-book-wrap ' + (applied ? 'is-ready' : '')}>
                  <CoverArt project={project} variant="final" />
                  <span className="final-stamp">{applied ? 'READY' : 'PREVIEW'}</span>
                  <div className="final-book-shadow" />
                </div>
                <div className="final-summary">
                  <div className="final-kicker">{applied ? 'MANUSCRIPT READY' : 'FINAL PREVIEW'}</div>
                  <h3>{applied ? '可以交给读者了。' : '确认前面的选择，生成一册干净的文稿。'}</h3>
                  <p>
                    {applied
                      ? '整合与校对已经写回卡片。下面这份文稿按幕与卡片顺序装订，可以导出为 Markdown 继续编辑。'
                      : '点击下方“应用所选并生成定稿”，会先把选中的合并与校对写回项目，再生成完整文稿。'}
                  </p>
                  <div className="final-stats">
                    <div><strong>{applied ? result.doc.count : estimatedCards}</strong><span>张卡片</span></div>
                    <div><strong>{applied ? result.doc.words : plan.stats.words}</strong><span>字</span></div>
                    <div><strong>{mergeCount + proofCount}</strong><span>项修改</span></div>
                  </div>
                  <div className="final-toc">
                    <div className="finish-minihead"><span>目录</span><em>{applied ? '已按幕装订' : '预计结构'}</em></div>
                    {(applied ? result.doc.sections : buildManuscript(project).sections).map((section) => (
                      <div className="toc-row" key={section.act}>
                        <span>{section.act}</span>
                        <i />
                        <em>{section.cards.length} 张卡片</em>
                      </div>
                    ))}
                  </div>
                  {applied ? (
                    <div className="final-actions">
                      <button className="btn btn--ink" onClick={downloadManuscript}><Download size={15} /> 导出 Markdown</button>
                      <button className="btn btn--outline" onClick={copyManuscript}>{copied ? <Check size={15} /> : <Copy size={15} />}{copied ? '已复制' : '复制全文'}</button>
                      <button className="final-ask-ai" onClick={() => onOpenRoundtable(project.cards.slice(0, 3).map((card) => card.id))}>
                        再和圆桌聊一次 <ArrowRight size={13} />
                      </button>
                    </div>
                  ) : (
                    <div className="final-pending">
                      <Sparkles size={15} />
                      <span>还差最后一步。应用后会生成完整文稿并写回项目。</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </main>
        </div>

        <footer className="finish-footer">
          <div className="finish-footer__left">
            {applied ? (
              <span className="finish-done"><CheckCircle2 size={16} /> 定稿已写回手稿，共 {result.doc.count} 张卡片。</span>
            ) : (
              <span>AI 已读完 {project.cards.length} 张卡片 · 已选择 <strong>{mergeCount}</strong> 组合并、<strong>{proofCount}</strong> 处校对</span>
            )}
          </div>
          <div className="finish-footer__actions">
            {!applied ? (
              <>
                <button className="btn btn--ghost" onClick={onClose}>暂不整理</button>
                <button className="btn btn--ink" onClick={applyAll}><FileCheck2 size={15} /> 应用所选并生成定稿</button>
              </>
            ) : (
              <>
                <button className="btn btn--outline" onClick={onClose}>回到手稿</button>
                <button className="btn btn--ink" onClick={downloadManuscript}><Download size={15} /> 导出文稿</button>
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  )
}
